import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ExternalLink, PlayCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { updatePropertyVideos } from '../../../services/propertyService';

const VideoManagementModal = ({ isOpen, onClose, property, onUpdate }) => {
  const [videos, setVideos] = useState([]);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Initialize videos from property with enhanced null handling
  useEffect(() => {
    if (property?.videos) {
      // Handle both array and null/undefined cases
      if (Array.isArray(property?.videos)) {
        setVideos(property?.videos);
      } else if (property?.videos === null || property?.videos === undefined) {
        setVideos([]);
      } else if (typeof property?.videos === 'object') {
        // Single video object - convert to array
        setVideos([property?.videos]);
      } else {
        setVideos([]);
      }
    } else {
      setVideos([]);
    }
  }, [property]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setNewVideoUrl('');
      setNewVideoTitle('');
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  const validateVideoUrl = (url) => {
    if (!url) return false;
    
    // Check both the full URL and the path before query params
    const urlLower = url?.toLowerCase();
    const pathBeforeQuery = urlLower?.split('?')?.[0];
    
    // Blocklist for non-video 3D formats (excluding .obj, .glb, .gltf which are accepted as 3D viewers)
    const blockedExtensions = ['.fbx', '.stl', '.dae', '.3ds', '.blend'];
    const isBlockedFile = blockedExtensions?.some(ext => 
      pathBeforeQuery?.endsWith(ext) || pathBeforeQuery?.includes(ext + '?') || pathBeforeQuery?.includes(ext + '&')
    );
    if (isBlockedFile) return false;

    // Block other non-video file extensions
    const nonVideoExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.zip', '.rar', '.txt', '.csv'];
    const isNonVideoFile = nonVideoExtensions?.some(ext => pathBeforeQuery?.endsWith(ext));
    if (isNonVideoFile) return false;

    // Support for common video formats and platforms (including .obj, .glb, .gltf for 3D viewing)
    const videoPatterns = [
      /^https?:\/\/.+\.(mp4|webm|ogg|mov|avi|mkv|obj|glb|gltf)(\?.*)?$/i, // Direct video/3D files
      /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+$/i, // YouTube
      /^https?:\/\/(www\.)?vimeo\.com\/.+$/i, // Vimeo
      /^https?:\/\/.+/, // Any other HTTP URL (without blocked extensions)
    ];

    return videoPatterns?.some(pattern => pattern?.test(url));
  };

  const handleAddVideo = () => {
    if (!validateVideoUrl(newVideoUrl)) {
      setError('URL vidéo invalide. Les images et autres formats non-vidéo ne sont pas acceptés. Veuillez entrer une URL vidéo valide (mp4, webm, mov, obj, etc.).');
      return;
    }

    const newVideo = {
      id: `video_${Date.now()}`,
      url: newVideoUrl?.trim(),
      title: newVideoTitle?.trim() || `Vidéo ${videos?.length + 1}`,
      description: '',
      added_at: new Date()?.toISOString()
    };

    setVideos([...videos, newVideo]);
    setNewVideoUrl('');
    setNewVideoTitle('');
    setError(null);
    setSuccess('Vidéo ajoutée! N\'oubliez pas de sauvegarder.');
  };

  const handleRemoveVideo = (videoId) => {
    setVideos(videos?.filter(video => video?.id !== videoId));
    setSuccess('Vidéo supprimée! N\'oubliez pas de sauvegarder.');
  };

  const handleUpdateVideo = (videoId, field, value) => {
    // If editing the URL field, validate it's not a blocked format
    if (field === 'url' && value) {
      const urlLower = value?.toLowerCase();
      const pathBeforeQuery = urlLower?.split('?')?.[0];
      const blockedExtensions = ['.fbx', '.stl', '.dae', '.3ds', '.blend'];
      const isBlockedFile = blockedExtensions?.some(ext =>
        pathBeforeQuery?.endsWith(ext) || pathBeforeQuery?.includes(ext + '?')
      );
      if (isBlockedFile) {
        setError('Les formats 3D non-vidéo (.fbx, .stl, etc.) ne sont pas acceptés. Les fichiers .obj, .glb et .gltf sont acceptés.');
        return;
      }
    }
    setVideos(videos?.map(video => 
      video?.id === videoId 
        ? { ...video, [field]: value, updated_at: new Date()?.toISOString() }
        : video
    ));
  };

  const handleSave = async () => {
    if (!property?.id) {
      setError('ID de propriété manquant');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Enhanced error handling for JSON coercion issues
      const { data, error: updateError } = await updatePropertyVideos(property?.id, videos);

      if (updateError) {
        // Specific handling for coercion errors
        if (updateError?.includes('coerce') || updateError?.includes('JSON')) {
          setError('Erreur de format des données vidéo. Les données ont été réinitialisées.');
          // Reset videos to empty array and try again
          const { error: retryError } = await updatePropertyVideos(property?.id, []);
          if (!retryError) {
            setVideos([]);
            setSuccess('Vidéos réinitialisées. Vous pouvez maintenant ajouter de nouvelles vidéos.');
            return;
          }
        } else {
          setError(`Erreur lors de la mise à jour: ${updateError}`);
        }
        return;
      }

      setSuccess('Vidéos mises à jour avec succès!');
      
      // Call onUpdate callback if provided
      if (onUpdate) {
        onUpdate();
      }

      // Close modal after successful update
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (err) {
      console.error('Video update error:', err);
      if (err?.message?.includes('coerce') || err?.message?.includes('JSON')) {
        setError('Erreur de format JSON détectée. Tentative de réparation...');
        // Attempt to fix by resetting videos
        try {
          await updatePropertyVideos(property?.id, []);
          setVideos([]);
          setError('Données vidéo réparées. Veuillez réessayer d\'ajouter vos vidéos.');
        } catch (fixError) {
          setError('Impossible de réparer les données vidéo. Contactez le support technique.');
        }
      } else {
        setError('Erreur lors de la sauvegarde des vidéos');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Gestion des vidéos - {property?.title}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Ajoutez, modifiez ou supprimez les vidéos de cette propriété
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Add New Video Section */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center">
              <Plus size={20} className="mr-2 text-blue-600" />
              Ajouter une nouvelle vidéo
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de la vidéo *
                </label>
                <Input
                  type="url"
                  value={newVideoUrl}
                  onChange={(e) => setNewVideoUrl(e?.target?.value)}
                  placeholder="https://example.com/video.mp4, lien YouTube/Vimeo, ou fichier .glb/.obj"
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titre de la vidéo
                </label>
                <Input
                  type="text"
                  value={newVideoTitle}
                  onChange={(e) => setNewVideoTitle(e?.target?.value)}
                  placeholder="Titre descriptif (optionnel)"
                  className="w-full"
                />
              </div>
            </div>

            <Button
              onClick={handleAddVideo}
              disabled={!newVideoUrl}
              className="mt-3 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus size={16} className="mr-2" />
              Ajouter la vidéo
            </Button>

            {/* Inline save button shown right after a video is added */}
            {success && success?.includes('ajoutée') && (
              <div className="mt-3 flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-700 font-medium flex-1">{success}</p>
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap"
                >
                  {loading ? 'Sauvegarde...' : '💾 Sauvegarder'}
                </Button>
              </div>
            )}
          </div>

          {/* Current Videos */}
          <div>
            <h3 className="font-medium text-gray-900 mb-3">
              Vidéos actuelles ({videos?.length})
            </h3>

            {videos?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <PlayCircle size={48} className="mx-auto mb-3 opacity-50" />
                <p>Aucune vidéo ajoutée pour cette propriété</p>
                <p className="text-sm">Utilisez le formulaire ci-dessus pour ajouter une vidéo</p>
              </div>
            ) : (
              <div className="space-y-4">
                {videos?.map((video, index) => (
                  <div key={video?.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <PlayCircle size={20} className="text-blue-600 mr-2" />
                        <span className="font-medium text-gray-900">
                          Vidéo {index + 1}
                        </span>
                        {video?.url && /\.(glb|gltf)(\?.*)?$/i?.test(video?.url) && (
                          <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                            3D GLB
                          </span>
                        )}
                        {video?.url && /\.obj(\?.*)?$/i?.test(video?.url) && (
                          <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full font-medium">
                            3D OBJ
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveVideo(video?.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Supprimer cette vidéo"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          URL
                        </label>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="url"
                            value={video?.url}
                            onChange={(e) => handleUpdateVideo(video?.id, 'url', e?.target?.value)}
                            className="flex-1"
                          />
                          <a
                            href={video?.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Ouvrir dans un nouvel onglet"
                          >
                            <ExternalLink size={16} />
                          </a>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Titre
                        </label>
                        <Input
                          type="text"
                          value={video?.title}
                          onChange={(e) => handleUpdateVideo(video?.id, 'title', e?.target?.value)}
                          placeholder="Titre de la vidéo"
                        />
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description (optionnelle)
                      </label>
                      <textarea
                        value={video?.description || ''}
                        onChange={(e) => handleUpdateVideo(video?.id, 'description', e?.target?.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="Description de la vidéo..."
                      />
                    </div>

                    {video?.added_at && (
                      <div className="mt-2 text-xs text-gray-500">
                        Ajoutée le: {new Date(video.added_at)?.toLocaleDateString('fr-FR')}
                        {video?.updated_at && video?.updated_at !== video?.added_at && (
                          <span className="ml-2">
                            • Modifiée le: {new Date(video.updated_at)?.toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          {/* Status Messages - always visible above the buttons */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center justify-between">
              <p className="text-sm text-green-700 font-medium">{success}</p>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {videos?.length} vidéo(s) • Formats supportés: MP4, YouTube, Vimeo, GLB, OBJ, liens directs
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSave}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoManagementModal;